import { useState, useRef, useEffect } from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';

interface CategoryFilterProps {
  categories: string[];
  selectedCategories: string[];
  onSelectionChange: (selected: string[]) => void;
}

export const CategoryFilter = ({
  categories,
  selectedCategories,
  onSelectionChange,
}: CategoryFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const allSelected = selectedCategories.length === categories.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggleAll = () => {
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange([...categories]);
    }
  };

  const handleToggleCategory = (category: string) => {
    if (selectedCategories.includes(category)) {
      const newSelection = selectedCategories.filter((c) => c !== category);
      onSelectionChange(newSelection);
    } else {
      const newSelection = [...selectedCategories, category];
      if (newSelection.length === categories.length) {
        onSelectionChange([...categories]);
      } else {
        onSelectionChange(newSelection);
      }
    }
  };

  const getDisplayText = () => {
    if (allSelected || selectedCategories.length === 0) {
      return 'Categories';
    }
    if (selectedCategories.length === 1) {
      return selectedCategories[0];
    }
    return `${selectedCategories.length} categories`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <CaretDown
        size={18}
        aria-hidden="true"
        className="text-text-body pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
      />
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="border-ui-border text-body text-text-body focus:ring-action-link flex appearance-none items-center gap-2 rounded border bg-white py-2 pl-9 pr-4 focus:outline-none focus:ring-2"
      >
        {getDisplayText()}
      </button>

      {isOpen && (
        <div className="border-ui-border absolute left-0 top-full z-10 mt-1 max-h-64 w-64 overflow-y-auto rounded border bg-white shadow-lg">
          <label className="hover:bg-ui-background flex cursor-pointer items-center gap-2 border-b px-4 py-2">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={handleToggleAll}
              className="h-4 w-4 rounded border-gray-300"
            />
            <span className="text-body font-semibold">All Categories</span>
            {allSelected && (
              <Check
                size={16}
                aria-hidden="true"
                className="text-brand-primary ml-auto"
              />
            )}
          </label>

          {categories.map((category) => (
            <label
              key={category}
              className="hover:bg-ui-background flex cursor-pointer items-center gap-2 px-4 py-2"
            >
              <input
                type="checkbox"
                checked={selectedCategories.includes(category)}
                onChange={() => handleToggleCategory(category)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <span className="text-body">{category}</span>
              {selectedCategories.includes(category) && (
                <Check
                  size={16}
                  aria-hidden="true"
                  className="text-brand-primary ml-auto"
                />
              )}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};
